/**
 * Editor `VectorPath` → PSD `LayerVectorMask` + `vectorFill` +
 * `vectorStroke`. Inverse of `psd-vector-import.ts::parsePsdVector`.
 * Tier 2 Group F PSD natives follow-up (Phase C).
 *
 * The export path takes the editor's `VectorPath` (subpaths +
 * anchors + optional stroke + optional fill) and produces the
 * three ag-psd chunks Photoshop reads as a native vector layer:
 *
 *  - `vectorMask.paths: BezierPath[]` — one path per editor sub-
 *    path. Anchor's relative `inX/inY/outX/outY` reversed back to
 *    absolute control-point coordinates for PSD's
 *    `[x0, y0, x1, y1, x2, y2]` knot layout.
 *  - `vectorFill: VectorContent` — solid color only. The editor's
 *    fill is `string | ShapeFillValue`; gradient `ShapeFillValue`
 *    instances aren't mapped (PSD's gradient fill records carry
 *    their own complex shape; out of v1 scope).
 *  - `vectorStroke` — line width, cap, join when the editor's
 *    `VectorStroke` is present.
 *
 * Pure module — no DOM. Tests inject editor `VectorPath` shapes
 * and assert on the resulting PSD chunks.
 */

import type { BezierKnot, BezierPath, Color, Layer as PsdLayer, VectorContent } from 'ag-psd';
import type { ShapeFillValue, VectorAnchor, VectorPath } from '../types';
import { hexToPsdColor } from './psd-color-utils';

/** Result of the editor-vector → PSD-chunks build. The three
 *  fields target separate slots on ag-psd's `Layer`; the caller
 *  spreads them onto the layer record. */
export interface PsdVectorChunks {
  vectorMask: NonNullable<PsdLayer['vectorMask']>;
  vectorFill?: VectorContent;
  vectorStroke?: PsdLayer['vectorStroke'];
  /** When present, ag-psd reads vector-stroke color/content from
   *  this slot (separate from `vectorStroke` which only carries
   *  line properties). */
  vectorStrokeContent?: VectorContent;
}

/** Build ag-psd's vector chunks from the editor's `VectorPath`.
 *  Returns null when the path has no sub-paths (the caller
 *  shouldn't have invoked us in that case, but defensive). */
export function buildPsdVectorChunks(path: VectorPath): PsdVectorChunks | null {
  if (!path.subPaths || path.subPaths.length === 0) return null;

  const psdPaths: BezierPath[] = path.subPaths.map((subPath) => ({
    open: !subPath.closed,
    fillRule: 'even-odd',
    knots: subPath.anchors.map(anchorToBezierKnot),
  }));

  const chunks: PsdVectorChunks = {
    vectorMask: { paths: psdPaths },
  };

  // Fill: only solid color round-trips. Gradient ShapeFillValue is
  // lossy on PSD (would need its own `vectorFill: { type: 'gradient' }`
  // shape that's out of scope here).
  const fillColor = resolveSolidFill(path.fill);
  if (fillColor !== undefined) {
    chunks.vectorFill = { type: 'color', color: fillColor };
  }

  if (path.stroke && path.stroke.width > 0) {
    chunks.vectorStroke = {
      strokeEnabled: true,
      fillEnabled: true,
      lineWidth: { units: 'Pixels', value: path.stroke.width },
      ...(path.stroke.cap ? { lineCapType: path.stroke.cap } : {}),
      ...(path.stroke.join ? { lineJoinType: path.stroke.join } : {}),
      lineAlignment: 'center',
    };
    chunks.vectorStrokeContent = { type: 'color', color: hexToPsdColor(path.stroke.color) };
  }

  return chunks;
}

/** Reverse of `bezierKnotToAnchor` in `psd-vector-import.ts`. Adds
 *  the anchor's relative `inX/inY/outX/outY` deltas back onto the
 *  absolute anchor to produce PSD's [x0, y0, x1, y1, x2, y2]
 *  layout. Missing handles default to coincident-with-anchor
 *  (matching PSD's encoding of a corner). */
function anchorToBezierKnot(anchor: VectorAnchor): BezierKnot {
  const ax = anchor.x;
  const ay = anchor.y;
  const inAbsX = ax + (anchor.inX ?? 0);
  const inAbsY = ay + (anchor.inY ?? 0);
  const outAbsX = ax + (anchor.outX ?? 0);
  const outAbsY = ay + (anchor.outY ?? 0);
  // `linked` mirrors the editor's `'smooth'` type. The other
  // editor types (`'corner'` and `'asymmetric'`) round-trip as
  // unlinked — PSD's linked flag just affects how Photoshop's
  // direct selection tool drags the handles together.
  const linked = anchor.type === 'smooth';
  return {
    linked,
    points: [inAbsX, inAbsY, ax, ay, outAbsX, outAbsY],
  };
}

/** Resolve the editor's `VectorPath['fill']` slot to an RGB Color
 *  for PSD's `vectorFill: { type: 'color', color }` chunk. Returns
 *  undefined when the fill is omitted, transparent, or a gradient
 *  (the editor's gradient fill maps to a richer PSD vectorFill
 *  shape that's out of v1 scope). */
function resolveSolidFill(fill: string | ShapeFillValue | undefined): Color | undefined {
  if (fill === undefined) return undefined;
  if (typeof fill === 'string') {
    if (fill === 'transparent' || fill === 'none') return undefined;
    return hexToPsdColor(fill);
  }
  if (fill.type === 'solid') {
    return hexToPsdColor(fill.color);
  }
  // Gradient / pattern fills → undefined here; caller may
  // surface a warning. Stash-restore on the export side would
  // preserve the original PSD vectorFill for unedited layers.
  return undefined;
}

// hexToPsdColor imported from psd-color-utils.ts.
